import React, { useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { fetchOptions } from '../queries';
import Select from './Select';

const SelectWithFetch = ({ name, query, props, user, filterRef, disableIfDefault, disableOnEdit, isEdit, defaultToReferenceField }) => {
    const [options, setOptions] = useState();
    const {
        clearErrors,
        getValues,
        formState: { dirtyFields, errors, defaultValues },
        selectedValues,
        setError,
        setValue,
        updateSelectedValues,
        watch,
    } = useFormContext();

    // TODO: Move into "queryParser" type of helper to be used throughout config'd components
    const { endpoint, where = {}, fields, order, labelKey, roleConstraints, populate, useLoopback, interval, includeDeleted } = query;
    const { referenceField, referenceKey, filterKey, autoSelect } = filterRef || {};
    const { defaultUndefined } = props;

    useEffect(
        () => {
            let isMounted = true;
            let populatedReferenceValue;
            const reference = !!referenceField && selectedValues[referenceField];

            /** This is to handle if the referenceField value is set but the selectedValues state is empty */
            if (reference == null && getValues(referenceField) != null) {
                populatedReferenceValue = getValues(referenceField);
            }
            // grab the proper key of the reference field
            const referenceValue = reference?.[referenceKey] ?? populatedReferenceValue;
            const whereClause = { ...where };
            if (referenceValue && !disableIfDefault && !defaultValues[name]) {
                // extend where object for filtering options
                const whereValue = Array.isArray(referenceValue) ? referenceValue.join(',') : referenceValue;
                // Only apply filter if whereValue is non-empty (prevents hauling_id= empty string 500)
                // When referenceValue is an empty array, skip the filter entirely so all options
                // are returned (some NL routes have hauling_companies: [] but still need drivers)
                if (whereValue) {
                    whereClause[filterKey] = whereValue;
                }
            }

            const getOptions = async () => {
                const includeDeletedOnEdit = isEdit && includeDeleted;
                const optionsResponse = await fetchOptions({ endpoint, where: whereClause, fields, order, labelKey, roleConstraints, populate, user, useLoopback, interval, includeDeleted: includeDeletedOnEdit });
                if (!isMounted) return;
                if (optionsResponse.error) {
                    setOptions([]);
                    setError(name, { type: 'fetch options', message: `Could not fetch ${props.label} options` });
                } else {
                    setOptions(optionsResponse);
                    clearErrors([name]);

                    const existingValue = getValues(name);
                    // If a value is already selected and exists in the new options, keep that value
                    const keepExisting = optionsResponse.some((opt) => existingValue === (opt?.id || opt?._id));
                    if (keepExisting) {
                        return;
                    }

                    // Set default value from reference field if specified and no existing value
                    if (defaultToReferenceField) {
                        const requiresOverride = !existingValue || (typeof existingValue === 'object' && !Object.keys(existingValue).length);
                        if (!requiresOverride) {
                            return;
                        }
                        const defaultValue = getValues(defaultToReferenceField);
                        if (defaultValue) {
                            const matchingOption = optionsResponse.find((opt) => (opt?.id || opt?._id) === defaultValue);
                            if (matchingOption) {
                                updateSelectedValues(name, matchingOption);
                                setValue(name, matchingOption.id || matchingOption._id || (defaultUndefined ? undefined : null));
                                return;
                            }
                        }
                    }

                    // Autopopulate with first value of response, or clear field
                    if (autoSelect && referenceValue && optionsResponse.length) {
                        updateSelectedValues(name, optionsResponse[0]);
                        setValue(name, optionsResponse[0].id || optionsResponse[0]._id || (defaultUndefined ? undefined : null));
                    } else if (dirtyFields[referenceField]) {
                        updateSelectedValues(name, null);
                        setValue(name, defaultUndefined ? undefined : null);
                    }
                }
            };

            getOptions();

            return () => {
                isMounted = false;
            };
        },
        !!referenceField ? [watch(referenceField), user] : [user]
    );

    // Watch for changes in defaultToReferenceField and update current field if not set
    useEffect(() => {
        if (!defaultToReferenceField || !options) {
            return;
        }

        const currentValue = getValues(name);
        const defaultValue = getValues(defaultToReferenceField);

        // Only update if current field is empty and defaultToReferenceField has a value
        if (!currentValue && defaultValue) {
            const matchingOption = options.find((opt) => (opt?.id || opt?._id) === defaultValue);
            if (matchingOption) {
                updateSelectedValues(name, matchingOption);
                setValue(name, matchingOption.id || matchingOption._id || (defaultUndefined ? undefined : null));
            }
        }
    }, [watch(defaultToReferenceField), options]);

    useEffect(() => {
        if (referenceField) {
            return;
        }
        const existingValue = getValues(name);
        updateSelectedValues(name, existingValue);
    }, [watch(name)]);

    // TODO: If we want all select fields to append '(Deleted <resource>)' to label if option is deleted, we can move this logic to Select.jsx
    const formatDeletedLabel = (option, allOptions) => {
        let labelOption = { ...option };
        if (typeof option === 'string') {
            labelOption = allOptions?.find((opt) => (opt.id || opt._id) === option);
        }

        if (!labelOption || !Object.keys(labelOption).length) {
            return '';
        }

        let label = labelOption[labelKey ?? 'name'];

        // Append '(Deleted <resource>)' to label if option is deleted
        if (includeDeleted && labelOption?.deleted_at) {
            label = `${label} (Deleted ${props.label})`;
        }
        return label;
    };

    const disableField = (disableOnEdit && isEdit) || props.disabled;

    return (
        <Select
            name={name}
            labelKey={labelKey}
            props={{ ...props, options, disabled: disableField }}
            loading={!options && !errors[name]}
            formatLabel={includeDeleted && formatDeletedLabel}
            // wrap
        />
    );
};

export default SelectWithFetch;
